import React from "react";
import { useTranslation } from "react-i18next";
import {
  EmptyStateIllustration,
  ErrorBadge,
  NoDataBadge,
  TableSearchEmptyIllustration
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";

export type DreamerAttachmentEmptyVariant =
  | "noData"
  | "error"
  | "filters"
  | "search";

interface Props {
  variant: DreamerAttachmentEmptyVariant;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  className?: string;
}

const TRANSLATION_PREFIX = "chat.emptyState";

const DreamerAttachmentEmptyState: React.FC<Props> = ({
  variant,
  className
}) => {
  const { t } = useTranslation();

  const renderIllustration = () => {
    switch (variant) {
      case "error":
        return <ErrorBadge />;
      case "search":
        return <TableSearchEmptyIllustration />;
      case "filters":
        return <EmptyStateIllustration />;
      case "noData":
        return <NoDataBadge />;
      default:
        return <EmptyStateIllustration />;
    }
  };

  const title = t(`${TRANSLATION_PREFIX}.${variant}.title`);
  const description =
    variant === "noData"
      ? null
      : t(`${TRANSLATION_PREFIX}.${variant}.description`);

  const isHorizontal = variant === "filters" || variant === "search";

  const wrapperClass = `flex h-full min-h-[180px] w-full items-center justify-center ${
    className ?? ""
  }`;

  if (isHorizontal) {
    return (
      <div className={wrapperClass}>
        <div className="flex flex-col items-center text-center gap-1">
          <Text variant="body" className="normal-case">
            {title}
          </Text>
          {description && <Text variant="bodySecondary">{description}</Text>}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="flex flex-col items-center justify-center">
        {renderIllustration()}
        <div className="flex flex-col items-center text-center gap-1">
          <div className="flex flex-col gap-1">
            <Text
              variant={variant === "noData" ? "body" : "bodySecondary"}
              className="normal-case"
            >
              {title}
            </Text>
            {description && <Text variant="bodySecondary">{description}</Text>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DreamerAttachmentEmptyState;
